/**
 * RootLayout - Unified Application Root Wrapper
 *
 * Eliminates 325+ lines of duplicate authentication, providers, and bundle mode handling
 * across 5+ apps (admin, chat, home, developer, store).
 *
 * Provides:
 * - Authentication flow (session check, loading, redirects)
 * - QueryClient provider (shared instance)
 * - Error boundaries
 * - Toast and notification providers
 * - Bundle mode detection (abstracted from app logic)
 *
 * Usage:
 * ```tsx
 * // apps/admin/src/App.tsx
 * import { RootLayout } from '@room/shared-ui';
 *
 * export function App() {
 *   return (
 *     <RootLayout appName="Admin">
 *       <YourAppContent />
 *     </RootLayout>
 *   );
 * }
 * ```
 */

import { ReactNode, useMemo, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getSessionToken } from '@room/frontend-sdk';
import { LoadingScreen } from '../components/LoadingScreen';
import { AppErrorBoundary } from '../components/AppErrorBoundary';
import { ToastProvider } from '../components/UI/ToastProvider';
import { NotificationProvider } from '../components/UI/NotificationContext';
import { getLoginRedirectUrl, shouldRedirectToLogin } from '../utils/auth';
import { useSession } from '../hooks';
import { isBundleMode } from '../utils/bundleMode';

// Shared QueryClient instance across all apps
const sharedQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

export interface RootLayoutProps {
  /**
   * Application name for display in loading screens
   */
  appName: string;

  /**
   * Children to render after authentication
   */
  children: ReactNode;

  /**
   * Require authentication before rendering children
   * @default true
   */
  requireAuth?: boolean;

  /**
   * Custom loading message
   */
  loadingMessage?: string;

  /**
   * Custom redirect message
   */
  redirectMessage?: string;

  /**
   * Custom QueryClient instance (optional - uses shared by default)
   */
  queryClient?: QueryClient;

  /**
   * Show dev console in loading screen
   * @default true in standalone, false in bundle mode
   */
  showDevConsole?: boolean;

  /**
   * Additional providers to wrap around children
   */
  additionalProviders?: (children: ReactNode) => ReactNode;
}

/**
 * RootLayout component - unified authentication and provider wrapper
 */
export function RootLayout({
  appName,
  children,
  requireAuth = true,
  loadingMessage,
  redirectMessage,
  queryClient = sharedQueryClient,
  showDevConsole,
  additionalProviders,
}: RootLayoutProps) {
  const { isAuthenticated, isLoading, hasCheckedSession, error, refresh } = useSession();
  const returnTo = useMemo(() => window.location.pathname + window.location.search, []);
  const isRunningInBundle = isBundleMode();

  // Debug logging for bundle mode
  console.log(`[RootLayout ${appName}] State:`, {
    requireAuth,
    isAuthenticated,
    isLoading,
    hasCheckedSession,
    isRunningInBundle,
    hasToken: Boolean(getSessionToken()),
    error: error?.message,
  });

  // Determine if dev console should be shown (default: shown unless in bundle mode)
  const shouldShowDevConsole = showDevConsole ?? !isRunningInBundle;

  // Debug details for loading screen
  const debugDetails = (
    <ul style={{ margin: 0, paddingLeft: '16px' }}>
      <li>{`hasToken: ${Boolean(getSessionToken())}`}</li>
      <li>{`isLoading: ${isLoading}`}</li>
      <li>{`hasCheckedSession: ${hasCheckedSession}`}</li>
      <li>{`isAuthenticated: ${isAuthenticated}`}</li>
      {error ? <li>{`error: ${error.message}`}</li> : null}
    </ul>
  );

  // Handle authentication redirect
  useEffect(() => {
    if (requireAuth && hasCheckedSession && !isLoading && !isAuthenticated) {
      if (shouldRedirectToLogin(window.location.pathname)) {
        window.location.replace(getLoginRedirectUrl(returnTo));
      }
    }
  }, [hasCheckedSession, isAuthenticated, isLoading, returnTo, requireAuth]);

  // Loading state - checking session
  if (requireAuth && (!hasCheckedSession || isLoading)) {
    console.log(`[RootLayout ${appName}] Rendering LoadingScreen (checking session)`);
    return (
      <LoadingScreen
        title={`Loading ${appName}`}
        message={loadingMessage || `Checking your session before opening ${appName.toLowerCase()}.`}
        details={debugDetails}
        showDevConsole={shouldShowDevConsole}
      />
    );
  }

  // Not authenticated - redirecting
  if (requireAuth && !isAuthenticated) {
    console.log(`[RootLayout ${appName}] Rendering LoadingScreen (redirecting)`);
    return (
      <LoadingScreen
        title="Redirecting"
        message={redirectMessage || "Taking you to the login screen."}
        details={debugDetails}
        showDevConsole={shouldShowDevConsole}
        actions={
          error ? (
            <button
              type="button"
              onClick={() => refresh()}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              Retry session check
            </button>
          ) : null
        }
      />
    );
  }

  // Authenticated - render app with all providers
  console.log(`[RootLayout ${appName}] Rendering app content (authenticated)`);
  let content = children;

  // Apply additional providers if provided
  if (additionalProviders) {
    content = additionalProviders(content);
  }

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <NotificationProvider>
            {content}
          </NotificationProvider>
        </ToastProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

/**
 * RootLayout without authentication requirement
 * Useful for public pages or apps that handle auth differently
 */
export function RootLayoutPublic({
  children,
  queryClient = sharedQueryClient,
  additionalProviders,
}: {
  children: ReactNode;
  queryClient?: QueryClient;
  additionalProviders?: (children: ReactNode) => ReactNode;
}) {
  let content = children;

  if (additionalProviders) {
    content = additionalProviders(content);
  }

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <NotificationProvider>
            {content}
          </NotificationProvider>
        </ToastProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
